import { useEffect, useState, useCallback, useMemo } from 'react';
import { servicesContainer } from '@/core/di/services-container';
import { useApiTokensStore } from '../../stores/api-tokens-store';
import { ActorSelector } from '@/core/components/ActorSelector';
import { useOperatorStore } from '@/core/stores/operator-store';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Badge } from '@/core/components/ui/badge';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/core/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/core/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/core/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/core/components/ui/popover';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Plus, Trash2, Copy, Check, KeyRound, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { HTTP_METHODS } from '../../types/api-token-types';
import type { ApiToken, ApiTokenRelation } from '../../types/api-token-types';

// ── Sort helpers ─────────────────────────────────────────────────────────────

type SortDirection = 'asc' | 'desc' | null;
interface SortConfig { column: string; direction: SortDirection }

function useSortState() {
  const [sortConfig, setSortConfig] = useState<SortConfig>({ column: '', direction: null });
  const handleSort = useCallback((column: string) => {
    setSortConfig(prev => {
      if (prev.column !== column) return { column, direction: 'asc' };
      if (prev.direction === 'asc') return { column, direction: 'desc' };
      return { column: '', direction: null };
    });
  }, []);
  return { sortConfig, handleSort };
}

function SortIcon({ column, sortConfig }: { column: string; sortConfig: SortConfig }) {
  return (
    <div className="flex flex-col items-center ml-1">
      <ChevronUp className={cn('h-3 w-3', sortConfig.column === column && sortConfig.direction === 'asc' ? GRID_STYLES.sort.active : GRID_STYLES.sort.inactive)} />
      <ChevronDown className={cn('h-3 w-3 -mt-1', sortConfig.column === column && sortConfig.direction === 'desc' ? GRID_STYLES.sort.active : GRID_STYLES.sort.inactive)} />
    </div>
  );
}

function SortableTh({ column, label, className, sortConfig, onSort, centered }: {
  column: string; label: string; className?: string; sortConfig: SortConfig; onSort: (col: string) => void; centered?: boolean;
}) {
  return (
    <th
      className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, GRID_STYLES.header.hoverText, 'cursor-pointer select-none', className)}
      onClick={() => onSort(column)}
    >
      <div className={cn('flex items-center', centered && 'justify-center')}>{label}<SortIcon column={column} sortConfig={sortConfig} /></div>
    </th>
  );
}

// ── Searchable select ─────────────────────────────────────────────────────────

interface IdNameItem { id: number; name: string }

function SearchableIdSelect({
  items,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  emptyText = 'No items available',
}: {
  items: IdNameItem[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  emptyText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return [...items].sort((a, b) => a.name.localeCompare(b.name));
    return items
      .map(item => {
        const n = item.name.toLowerCase();
        const id = String(item.id);
        const score = n === q ? 100 : n.startsWith(q) ? 90 : n.includes(q) ? 70
          : id === q ? 85 : id.startsWith(q) ? 60 : id.includes(q) ? 40 : 0;
        return { item, score };
      })
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name))
      .map(r => r.item);
  }, [items, search]);

  const selected = items.find(i => String(i.id) === value);

  return (
    <Popover modal={false} open={open && !disabled} onOpenChange={o => { if (!disabled) { setOpen(o); if (!o) setSearch(''); } }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between font-normal"
          onClick={() => !disabled && setOpen(o => !o)}
        >
          <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
            {selected ? selected.name : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Type to search…" value={search} onValueChange={setSearch} />
          <CommandList className="max-h-60 overflow-y-auto" onWheel={e => e.stopPropagation()}>
            <CommandEmpty>
              <p className="text-sm text-gray-500 text-center py-4">{emptyText}</p>
            </CommandEmpty>
            <CommandGroup>
              <CommandItem value="__none__" onSelect={() => { onChange(''); setOpen(false); setSearch(''); }}
                className="flex items-center gap-2 cursor-pointer">
                <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                <span className="text-sm text-gray-400">none</span>
              </CommandItem>
              {filtered.map(item => (
                <CommandItem
                  key={item.id}
                  value={`${item.name} ${item.id}`}
                  onSelect={() => { onChange(String(item.id)); setOpen(false); setSearch(''); }}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Check className={cn('mr-2 h-4 w-4', value === String(item.id) ? 'opacity-100' : 'opacity-0')} />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{item.name}</span>
                    <span className="text-xs text-gray-500">ID: {item.id}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

type RelationRow = ApiTokenRelation & { token: ApiToken };

// ── Main Page ─────────────────────────────────────────────────────────────────

export function ApiTokensPage() {
  const { operators, isLoading: operatorsLoading, loadOperators } = useOperatorStore();

  const {
    tokens, isLoading, isSaving, error,
    newlyCreatedToken, deleteTokenId, deleteRelationIds, deleteEndpointIds,
    createToken, deleteToken, addRelation, deleteRelation, addEndpoint, deleteEndpoint,
    clearNewlyCreatedToken,
    confirmDeleteToken, cancelDeleteToken,
    confirmDeleteRelation, cancelDeleteRelation,
    confirmDeleteEndpoint, cancelDeleteEndpoint,
    fetchTokens, reset, clearError,
  } = useApiTokensStore();

  const { sortConfig, handleSort } = useSortState();

  // ── Table-level distributor filter ─────────────────────────────────────────

  const [filterDistributorId, setFilterDistributorId] = useState<number | null>(null);

  // ── All relations, optionally filtered by distributor ──────────────────────

  const filteredRows = useMemo((): RelationRow[] => {
    const all = tokens.flatMap(token =>
      token.relations.map(r => ({ ...r, token }))
    );
    if (!filterDistributorId) return all;
    return all.filter(r => r.distributorId === filterDistributorId);
  }, [tokens, filterDistributorId]);

  //  Name resolution
  const [namesCache, setNamesCache] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    const distributorIds = [...new Set(
      tokens.flatMap(t => t.relations.map(r => r.distributorId).filter((id): id is number => id != null))
    )];
    if (!distributorIds.length) return;
    const { apiTokensApi } = servicesContainer.getServices();
    Promise.all(distributorIds.map(id => apiTokensApi.getHierarchyActors(id))).then(results => {
      setNamesCache(prev => {
        const next = new Map(prev);
        results.forEach(actors => actors.forEach(a => {
          if (a.name) next.set(a.actorId, a.name);
          if (a.parentActorId != null && a.parentActorName) next.set(a.parentActorId, a.parentActorName);
        }));
        return next;
      });
    }).catch(() => {});
  }, [tokens]);

  const resolveName = useCallback((id: number | null): string => {
    if (!id) return '';
    const name = namesCache.get(id);
    return name ? `${name} (${id})` : String(id);
  }, [namesCache]);

  const resolveOperatorName = useCallback((operatorId: number | null): string => {
    if (!operatorId) return '';
    const op = operators.find(o => o.operatorId === operatorId);
    return op ? `${op.name} (${operatorId})` : String(operatorId);
  }, [operators]);

  // Distributors

  const [distributors, setDistributors] = useState<Array<{ id: number; name: string }>>([]);

  // Mount ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    const { apiTokensApi } = servicesContainer.getServices();
    apiTokensApi.getDistributors().then(setDistributors);
    loadOperators();
    fetchTokens();
    return () => reset();
  }, []);

  // Distributor lists ───────────────────────────────────────────────────────

  // Unique distributors that appear in token relations (for the table filter dropdown)
  const uniqueDistributors = useMemo(() => {
    const ids = [...new Set(
      tokens.flatMap(t => t.relations.map(r => r.distributorId).filter((id): id is number => id != null))
    )];
    return ids
      .map(id => {
        const found = distributors.find(d => d.id === id);
        return { id, name: found ? found.name : String(id) };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tokens, distributors]);

  // Add New Token modal ─────────────────────────────────────────────────────

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [modalDistributorId, setModalDistributorId] = useState<number | null>(null);
  const [modalOperatorId, setModalOperatorId] = useState('');
  const [modalExpiry, setModalExpiry] = useState('');
  const [modalActorId, setModalActorId] = useState('');
  const [modalEndpointUrl, setModalEndpointUrl] = useState('');
  const [modalEndpointMethod, setModalEndpointMethod] = useState('');
  const [hierarchyActors, setHierarchyActors] = useState<Array<{ actorId: number; name: string | null; parentActorId?: number | null }>>([]);

  // All distributors for the sheet
  const modalDistributors = useMemo(
    () => [...distributors].sort((a, b) => a.name.localeCompare(b.name)),
    [distributors]
  );

  // Operators filtered by distributor selected in the sheet
  const sheetOperators = useMemo(
    () => modalDistributorId != null ? operators.filter(o => o.distributorId === modalDistributorId) : operators,
    [operators, modalDistributorId]
  );

  const sheetOperatorItems = useMemo(
    (): IdNameItem[] => sheetOperators.map(o => ({ id: o.operatorId, name: o.name })),
    [sheetOperators]
  );

  const modalOperator = useMemo(
    () => operators.find(o => o.operatorId.toString() === modalOperatorId) ?? null,
    [operators, modalOperatorId]
  );

  // Reload hierarchy when operator changes inside the sheet
  useEffect(() => {
    if (!modalOperator) { setHierarchyActors([]); return; }
    const { apiTokensApi } = servicesContainer.getServices();
    apiTokensApi.getHierarchyActors(modalOperator.distributorId).then(setHierarchyActors).catch(() => {});
  }, [modalOperatorId]);

  const actorsList = useMemo((): IdNameItem[] => {
    if (!modalOperator) return [];
    const getAllDescendants = (parentId: number): IdNameItem[] => {
      const direct = hierarchyActors.filter(a => a.parentActorId === parentId);
      return [
        ...direct.map(a => ({ id: a.actorId, name: a.name ?? String(a.actorId) })),
        ...direct.flatMap(a => getAllDescendants(a.actorId)),
      ];
    };
    return getAllDescendants(modalOperator.operatorId).sort((a, b) => a.name.localeCompare(b.name));
  }, [hierarchyActors, modalOperator]);

  const handleOpenAddModal = () => {
    const preDistributorId = filterDistributorId;
    setModalDistributorId(preDistributorId);
    // Auto-select operator only if there's exactly one under the pre-selected distributor
    const distOperators = preDistributorId != null
      ? operators.filter(o => o.distributorId === preDistributorId)
      : operators;
    const preselect = distOperators.length === 1 ? distOperators[0] : null;
    setModalOperatorId(preselect?.operatorId.toString() ?? '');
    setModalExpiry('');
    setModalActorId('');
    setModalEndpointUrl('');
    setModalEndpointMethod('');
    setAddModalOpen(true);
  };

  const handleAddModalSubmit = async () => {
    const success = await createToken({ expirationDate: modalExpiry || null });
    if (!success) return;

    const { tokens: updatedTokens } = useApiTokensStore.getState();
    const newTokenId = Math.max(...updatedTokens.map(t => t.tokenId));

    if (modalOperator) {
      await addRelation(newTokenId, {
        distributorId: modalOperator.distributorId,
        operatorId: modalOperator.operatorId,
        actorId: modalActorId ? Number(modalActorId) : null,
      });
    }

    if (modalEndpointUrl.trim()) {
      await addEndpoint(newTokenId, {
        endpointURL: modalEndpointUrl.trim(),
        httpMethod: modalEndpointMethod || null,
      });
    }

    setAddModalOpen(false);
  };

  //Add Endpoint to existing token 
  const [epDialogTokenId, setEpDialogTokenId] = useState<number | null>(null);
  const [epUrl, setEpUrl] = useState('');
  const [epMethod, setEpMethod] = useState('');

  const handleOpenEpDialog = (tokenId: number) => {
    setEpUrl('');
    setEpMethod('');
    setEpDialogTokenId(tokenId);
  };

  const handleSubmitEpDialog = async () => {
    if (epDialogTokenId == null || !epUrl.trim()) return;
    const ok = await addEndpoint(epDialogTokenId, { endpointURL: epUrl.trim(), httpMethod: epMethod || null });
    if (ok) setEpDialogTokenId(null);
  };

  const handleConfirmDeleteEndpoint = async () => {
    if (deleteEndpointIds) await deleteEndpoint(deleteEndpointIds.tokenId, deleteEndpointIds.endpointId);
  };

  // Copy token secret ───────────────────────────────────────────────────────

  const [copiedSecret, setCopiedSecret] = useState(false);

  const handleCopySecret = async () => {
    if (!newlyCreatedToken) return;
    let copied = false;

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(newlyCreatedToken);
        copied = true;
      } catch {
        // fall through
      }
    }

    if (!copied) {
      const codeEl = document.querySelector('[role="dialog"] code');
      if (codeEl) {
        const range = document.createRange();
        range.selectNodeContents(codeEl);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        try { copied = document.execCommand('copy'); } finally { selection?.removeAllRanges(); }
      }
    }

    if (copied) {
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  // Delete handlers ─────────────────────────────────────────────────────────

  const handleConfirmDeleteToken = async () => {
    if (deleteTokenId != null) await deleteToken(deleteTokenId);
  };

  const handleConfirmDeleteRelation = async () => {
    if (deleteRelationIds) await deleteRelation(deleteRelationIds.tokenId, deleteRelationIds.relationId);
  };

  // Sorted rows ─────────────────────────────────────────────────────────────

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      if (!sortConfig.column || !sortConfig.direction) return a.tokenId - b.tokenId;
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      if (sortConfig.column === 'tokenId') return (a.tokenId - b.tokenId) * dir;
      if (sortConfig.column === 'expiry') return ((a.token.expirationDate ?? '').localeCompare(b.token.expirationDate ?? '')) * dir;
      if (sortConfig.column === 'status') return (Number(b.token.isActive) - Number(a.token.isActive)) * dir;
      if (sortConfig.column === 'distributor') return resolveName(a.distributorId).localeCompare(resolveName(b.distributorId)) * dir;
      if (sortConfig.column === 'operator') return resolveOperatorName(a.operatorId).localeCompare(resolveOperatorName(b.operatorId)) * dir;
      if (sortConfig.column === 'actor') return resolveName(a.actorId).localeCompare(resolveName(b.actorId)) * dir;
      if (sortConfig.column === 'endpoints') return (a.token.endpoints.length - b.token.endpoints.length) * dir;
      if (sortConfig.column === 'created') return ((a.token.created ?? '').localeCompare(b.token.created ?? '')) * dir;
      return 0;
    });
  }, [filteredRows, sortConfig, resolveName, resolveOperatorName]);

  // Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between gap-4">
          <div className="border-l-4 border-yellow-400 pl-4 shrink-0">
            <h1 className="text-lg font-semibold text-gray-900">API Token Management</h1>
          </div>
          <Button
            onClick={handleOpenAddModal}
            size="sm"
            className="gap-2 bg-yellow-400 hover:bg-yellow-500 text-gray-900"
          >
            <Plus className="h-4 w-4" />
            Add New Token
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" onClick={clearError}>Dismiss</Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Table-level distributor filter */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm text-gray-500 shrink-0">Distributor:</span>
          <div className="w-64">
            <SearchableIdSelect
              items={modalDistributors}
              value={filterDistributorId != null ? String(filterDistributorId) : ''}
              onChange={v => setFilterDistributorId(v ? Number(v) : null)}
              placeholder="All distributors"
              emptyText="No distributors found"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-md" />)}
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <KeyRound className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm">{filterDistributorId != null ? 'No token relations for this distributor' : 'No API token relations found'}</p>
          </div>
        ) : (
          <div className={cn('border rounded-lg overflow-hidden', GRID_STYLES.border)}>
            <table className="w-full caption-bottom text-sm">
              <thead className={cn(GRID_STYLES.header.background, '[&_tr]:border-b')}>
                <tr className={cn(GRID_STYLES.header.background, 'border-b transition-colors')}>
                  <SortableTh column="tokenId" label="Token ID" className="w-[8%]" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableTh column="expiry" label="Expiration Date" className="w-[14%]" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableTh column="status" label="Status" className="w-[10%]" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableTh column="distributor" label="Distributor" className="w-[15%]" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableTh column="operator" label="Operator" className="w-[15%]" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableTh column="actor" label="Location" className="w-[15%]" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableTh column="endpoints" label="Endpoints" className="w-[10%]" centered sortConfig={sortConfig} onSort={handleSort} />
                  <SortableTh column="created" label="Created Date" className="w-[12%]" sortConfig={sortConfig} onSort={handleSort} />
                  <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-[8%] text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(rel => (
                  <tr key={rel.relationId} className={cn(GRID_STYLES.row.default, GRID_STYLES.row.hover, 'transition-colors border-b', GRID_STYLES.border)}>
                    <td className={cn(GRID_STYLES.cell.base, 'text-gray-500 text-xs font-mono')}>{rel.tokenId}</td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-gray-600 text-xs')}>{formatDate(rel.token.expirationDate)}</td>
                    <td className={GRID_STYLES.cell.base}>
                      <Badge
                        variant={rel.token.isActive ? 'default' : 'secondary'}
                        className={rel.token.isActive ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-gray-100 text-gray-600'}
                      >
                        {rel.token.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-gray-700 text-xs')}>{resolveName(rel.distributorId)}</td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-gray-700 text-xs')}>{resolveOperatorName(rel.operatorId)}</td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-gray-700 text-xs')}>{resolveName(rel.actorId)}</td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-center')}>
                      <Popover>
                        <PopoverTrigger asChild>
                          {rel.token.endpoints.length > 0 ? (
                            <button className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 font-mono text-xs px-2 py-0.5 hover:bg-blue-100 transition-colors cursor-pointer">
                              {rel.token.endpoints.length}
                            </button>
                          ) : (
                            <button className="text-xs text-gray-400 hover:text-blue-600 transition-colors cursor-pointer" title="Add endpoint">
                              + add
                            </button>
                          )}
                        </PopoverTrigger>
                        <PopoverContent className="w-[420px] p-0" align="center">
                          <div className="px-4 py-2.5 border-b bg-gray-50 rounded-t-md flex items-center justify-between">
                            <p className="text-xs font-semibold text-gray-600">Allowed Endpoints</p>
                            <span className="text-xs text-gray-400">Token {rel.tokenId}</span>
                          </div>
                          {rel.token.endpoints.length > 0 && (
                            <ul className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                              {rel.token.endpoints.map(ep => (
                                <li key={ep.endpointId} className="flex items-center gap-3 px-4 py-2.5 group">
                                  <span className="shrink-0 rounded bg-gray-100 text-gray-600 font-mono text-[10px] px-1 py-0.5 min-w-[36px] text-center">
                                    {ep.httpMethod ?? 'ANY'}
                                  </span>
                                  <span className="font-mono text-xs text-gray-800 break-all flex-1">{ep.endpointURL}</span>
                                  <button
                                    onClick={() => confirmDeleteEndpoint(ep.tokenId, ep.endpointId)}
                                    className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600"
                                    title="Delete endpoint"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          <div className="px-4 py-2.5 border-t">
                            <button
                              onClick={() => handleOpenEpDialog(rel.tokenId)}
                              className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                            >
                              <Plus className="h-3 w-3" />
                              Add Endpoint
                            </button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-gray-600 text-xs')}>{formatDate(rel.token.created)}</td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-right')}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmDeleteToken(rel.tokenId)}
                        disabled={isSaving}
                        title="Delete token"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Add New Token Sheet ──────────────────────────────────────────────── */}
      <Sheet open={addModalOpen} onOpenChange={open => { if (!isSaving) setAddModalOpen(open); }}>
        <SheetContent className="w-[420px] sm:w-[480px] overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle>Add New API Token</SheetTitle>
            <p className="text-sm text-gray-500">
              Select an operator, then optionally scope the token to a location and endpoint.
            </p>
          </SheetHeader>

          <div className="space-y-4 py-2">
            {/* Expiration Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Expiration Date</label>
              <Input
                type="date"
                value={modalExpiry}
                onChange={e => setModalExpiry(e.target.value)}
              />
            </div>

            {/* Distributor */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Distributor <span className="text-red-500">*</span>
              </label>
              <SearchableIdSelect
                items={modalDistributors}
                value={modalDistributorId != null ? String(modalDistributorId) : ''}
                onChange={v => {
                  setModalDistributorId(v ? Number(v) : null);
                  setModalOperatorId('');
                  setModalActorId('');
                }}
                placeholder="Select distributor…"
                emptyText="No distributors found"
              />
            </div>

            {/* Operator */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Operator</label>
              <SearchableIdSelect
                items={sheetOperatorItems}
                value={modalOperatorId}
                onChange={id => { setModalOperatorId(id); setModalActorId(''); }}
                placeholder="Select operator…"
                emptyText={modalDistributorId != null ? 'No operators found' : 'Select a distributor first'}
                disabled={modalDistributorId == null}
              />
            </div>

            {/* Location */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <SearchableIdSelect
                items={actorsList}
                value={modalActorId}
                onChange={setModalActorId}
                placeholder="No location restriction"
                emptyText="No locations found under this operator"
              />
            </div>

            {/* Endpoint URL */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Endpoint URL</label>
              <Input
                value={modalEndpointUrl}
                onChange={e => setModalEndpointUrl(e.target.value)}
                placeholder="/api/v1/…"
              />
            </div>

            {/* HTTP Method */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">HTTP Method</label>
              <Select
                value={modalEndpointMethod || '__any__'}
                onValueChange={v => setModalEndpointMethod(v === '__any__' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any method</SelectItem>
                  {HTTP_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setAddModalOpen(false)} disabled={isSaving}>Cancel</Button>
              <Button
                onClick={handleAddModalSubmit}
                disabled={isSaving || !modalDistributorId}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900"
              >
                {isSaving ? 'Creating…' : 'Create Token'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Newly Created Token Secret ───────────────────────────────────────── */}
      <Dialog open={!!newlyCreatedToken} onOpenChange={() => clearNewlyCreatedToken()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-green-700">Token Created Successfully</DialogTitle>
            <DialogDescription>
              Copy this token now — it will <strong>not</strong> be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 rounded-md border bg-gray-50 px-3 py-2">
              <code className="flex-1 text-xs font-mono break-all text-gray-800">{newlyCreatedToken}</code>
              <Button variant="ghost" size="sm" onClick={handleCopySecret} className="shrink-0">
                {copiedSecret ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={clearNewlyCreatedToken} className="bg-yellow-400 hover:bg-yellow-500 text-gray-900">
              I've copied the token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Token Confirmation ────────────────────────────────────────── */}
      <Dialog open={deleteTokenId != null} onOpenChange={cancelDeleteToken}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Token</DialogTitle>
            <DialogDescription>
              This will permanently delete token {deleteTokenId} and all its relations and endpoints. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelDeleteToken}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDeleteToken} disabled={isSaving}>
              {isSaving ? 'Deleting…' : 'Delete Token'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Relation Confirmation ─────────────────────────────────────── */}
      <Dialog open={deleteRelationIds != null} onOpenChange={cancelDeleteRelation}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Relation</DialogTitle>
            <DialogDescription>
              Remove relation {deleteRelationIds?.relationId}? The token itself will remain. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelDeleteRelation}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDeleteRelation} disabled={isSaving}>
              {isSaving ? 'Deleting…' : 'Delete Relation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Endpoint to Existing Token ──────────────────────────────────── */}
      <Dialog open={epDialogTokenId != null} onOpenChange={open => { if (!open && !isSaving) setEpDialogTokenId(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Allowed Endpoint</DialogTitle>
            <DialogDescription>
              Add an endpoint restriction to token {epDialogTokenId}. Leave method blank to allow all.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">URL pattern</label>
              <Input
                value={epUrl}
                onChange={e => setEpUrl(e.target.value)}
                placeholder="/api/v1/…"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">HTTP method</label>
              <Select value={epMethod || '__any__'} onValueChange={v => setEpMethod(v === '__any__' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Any method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any method</SelectItem>
                  {HTTP_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEpDialogTokenId(null)} disabled={isSaving}>Cancel</Button>
            <Button
              onClick={handleSubmitEpDialog}
              disabled={isSaving || !epUrl.trim()}
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900"
            >
              {isSaving ? 'Adding…' : 'Add Endpoint'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Endpoint Confirmation ─────────────────────────────────────── */}
      <Dialog open={deleteEndpointIds != null} onOpenChange={cancelDeleteEndpoint}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Endpoint</DialogTitle>
            <DialogDescription>
              Remove endpoint {deleteEndpointIds?.endpointId}? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelDeleteEndpoint}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDeleteEndpoint} disabled={isSaving}>
              {isSaving ? 'Deleting…' : 'Delete Endpoint'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
